/*
----------------------------------------------------------------------------
Safe renderer for the markdown subset returned by the AI agent.
----------------------------------------------------------------------------
Output is built with DOM APIs only (never innerHTML), so agent text can not
inject markup, scripts or remote embeds. Supported: paragraphs, headings,
bulleted and numbered lists, bold, italic, inline code, links to http(s)
or site-relative URLs and bare http(s)/www. URLs. Anything else is rendered
as plain text. Links open in a new tab so the conversation is never lost.
*/

// Bare URLs stop before closing punctuation so "see https://softcat.com." links without the full stop.
const INLINE_PATTERN = /(\[([^\]\n]+)\]\(([^)\s]+)\))|(\*\*([^*\n]+)\*\*)|(__([^_\n]+)__)|(\*([^*\n]+)\*)|(`([^`\n]+)`)|((?:https?:\/\/|www\.)[^\s<>()[\]{}"'`]*[^\s<>()[\]{}"'`.,;:!?*_])/

export const isSafeUrl = (url) => {
    if (/^\/(?!\/)/.test(url)) {
        return true
    }
    try {
        const parsed = new URL(url, window.location.origin)
        return parsed.protocol === 'https:' || parsed.protocol === 'http:'
    } catch {
        return false
    }
}

export const createLink = (text, url, className = '') => {
    const link = document.createElement('a')
    link.href = url
    link.textContent = text
    if (className) {
        link.className = className
    }
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    const hint = document.createElement('span')
    hint.className = 'sr-only'
    hint.textContent = ' (opens in a new tab)'
    link.appendChild(hint)
    return link
}

const renderInline = (text, parent) => {
    let remaining = text
    while (remaining.length) {
        const match = remaining.match(INLINE_PATTERN)
        if (!match) {
            parent.appendChild(document.createTextNode(remaining))
            return
        }
        if (match.index > 0) {
            parent.appendChild(document.createTextNode(remaining.slice(0, match.index)))
        }

        if (match[1]) {
            if (isSafeUrl(match[3])) {
                parent.appendChild(createLink(match[2], match[3]))
            } else {
                parent.appendChild(document.createTextNode(match[2]))
            }
        } else if (match[4] || match[6]) {
            const strong = document.createElement('strong')
            renderInline(match[5] || match[7], strong)
            parent.appendChild(strong)
        } else if (match[8]) {
            const em = document.createElement('em')
            renderInline(match[9], em)
            parent.appendChild(em)
        } else if (match[10]) {
            const code = document.createElement('code')
            code.textContent = match[11]
            parent.appendChild(code)
        } else if (match[12]) {
            const url = match[12].startsWith('www.') ? `https://${match[12]}` : match[12]
            parent.appendChild(isSafeUrl(url) ? createLink(match[12], url) : document.createTextNode(match[12]))
        }

        remaining = remaining.slice(match.index + match[0].length)
    }
}

const renderLines = (lines, parent) => {
    lines.forEach((line, index) => {
        if (index > 0) {
            parent.appendChild(document.createElement('br'))
        }
        renderInline(line, parent)
    })
}

const renderList = (lines, ordered) => {
    const list = document.createElement(ordered ? 'ol' : 'ul')
    const marker = ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*+]\s+/
    lines.forEach((line) => {
        const item = document.createElement('li')
        renderInline(line.replace(marker, ''), item)
        list.appendChild(item)
    })
    return list
}

export const renderMarkdown = (markdown) => {
    const fragment = document.createDocumentFragment()
    const blocks = String(markdown || '')
        .replace(/\r\n?/g, '\n')
        .split(/\n{2,}/)
        .map((block) => block.split('\n').filter((line) => line.trim() !== ''))
        .filter((lines) => lines.length)

    blocks.forEach((lines) => {
        const heading = lines[0].match(/^\s{0,3}(#{1,6})\s+(.*)$/)
        if (heading && lines.length === 1) {
            const level = Math.min(4, heading[1].length + 2)
            const el = document.createElement(`h${level}`)
            renderInline(heading[2], el)
            fragment.appendChild(el)
            return
        }
        if (lines.every((line) => /^\s*[-*+]\s+/.test(line))) {
            fragment.appendChild(renderList(lines, false))
            return
        }
        if (lines.every((line) => /^\s*\d+[.)]\s+/.test(line))) {
            fragment.appendChild(renderList(lines, true))
            return
        }
        const paragraph = document.createElement('p')
        renderLines(lines, paragraph)
        fragment.appendChild(paragraph)
    })

    return fragment
}
